import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Utensils, ShoppingBag, Sparkles, Coffee, Gift, MapPin, Wand2 } from "lucide-react";

/**
 * WelcomeCarousel — Mensaje de bienvenida rotante para la landing del QR.
 * Muestra 3 escenarios emotivos que motivan al usuario a activar el radar:
 *  1) Hambre/antojo (comida, cafés)
 *  2) Paseo por el mall (compras)
 *  3) Alerta de regalos (sorpresa)
 * Cada tarjeta tiene su propio gradiente, íconos animados y microtexto.
 */
const SLIDES = [
  {
    key: "hunger",
    eyebrow: "¿Tienes hambre o antojo?",
    emoji: "🍟☕",
    icons: [Utensils, Coffee, Gift],
    title: "Come rico. Paga menos.",
    body:
      "Escanea y descubre qué restaurantes y cafeterías de este pasillo te están regalando un descuento o un postre justo ahora.",
    from: "#FF007A",
    to: "#FFB020",
    accent: "#FFF0D6",
  },
  {
    key: "mall",
    eyebrow: "¿De paseo por la ciudad?",
    emoji: "🛍️✨",
    icons: [ShoppingBag, Sparkles, MapPin],
    title: "Las tiendas te están viendo.",
    body:
      "Las tiendas por las que vas a pasar — en la plaza, en la calle o en la avenida — tienen sorpresas para ti. Activa tu radar y recibe alertas exclusivas cuando camines cerca de ellas.",
    from: "#0055FF",
    to: "#8A5CFF",
    accent: "#DDE8FF",
  },
  {
    key: "alert",
    eyebrow: "¡Alerta de regalos!",
    emoji: "🚨🎁",
    icons: [Gift, Wand2, Sparkles],
    title: "Regalos activándose a 50 metros.",
    body:
      "Hay promociones exclusivas activándose a 50 metros a la redonda. Simplemente camina y nuestra IA te dirá qué tiendas te están buscando.",
    from: "#00C875",
    to: "#00A8E8",
    accent: "#D3FFF0",
  },
];

export default function WelcomeCarousel({ intervalMs = 5000 }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);

  const slide = SLIDES[index];

  return (
    <div className="relative" data-testid="welcome-carousel">
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.key}
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -18, scale: 0.98 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[28px] p-6 text-white shadow-xl"
          style={{
            background: `linear-gradient(135deg, ${slide.from} 0%, ${slide.to} 100%)`,
          }}
          data-testid={`welcome-slide-${slide.key}`}
        >
          {/* Decoración: blobs blancos difusos */}
          <div className="absolute -top-16 -right-10 w-48 h-48 rounded-full bg-white/15 blur-3xl" />
          <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-white/10 blur-3xl" />

          {/* Íconos flotantes decorativos */}
          <div className="absolute top-4 right-4 flex gap-1.5 opacity-80">
            {slide.icons.map((Ic, i) => (
              <motion.div
                key={`${slide.key}-icon-${Ic.displayName || Ic.name}`}
                initial={{ y: -6, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 + i * 0.1, duration: 0.4 }}
                className="w-9 h-9 rounded-xl grid place-items-center backdrop-blur-md"
                style={{ background: "rgba(255,255,255,0.22)" }}
              >
                <Ic className="w-4 h-4" />
              </motion.div>
            ))}
          </div>

          {/* Emoji hero grande */}
          <div className="text-5xl leading-none mb-3" aria-hidden>
            {slide.emoji}
          </div>

          <div className="text-xs uppercase tracking-widest font-black opacity-90 mb-2">
            {slide.eyebrow}
          </div>
          <h3 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight leading-tight">
            {slide.title}
          </h3>
          <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-white/90">
            {slide.body}
          </p>

          {/* Barra de progreso auto-play */}
          <div className="mt-5 h-1 rounded-full bg-white/20 overflow-hidden">
            <motion.div
              key={slide.key + "-bar"}
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: intervalMs / 1000, ease: "linear" }}
              className="h-full bg-white/80"
            />
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Dots */}
      <div className="mt-3 flex items-center justify-center gap-1.5">
        {SLIDES.map((s, i) => (
          <button
            key={s.key}
            onClick={() => setIndex(i)}
            aria-label={`Ir a ${s.eyebrow}`}
            data-testid={`welcome-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-6 bg-black" : "w-1.5 bg-neutral-300 hover:bg-neutral-400"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
